import Box from '@mui/material/Box';
import type { CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { noop } from '@openx/utils/noop';
import groupBy from 'lodash/groupBy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomVariablesList } from '../../CustomVariables/CustomVariablesList';
import { EmptyFilterMessage } from '../../shared/EmptyFilterMessage/EmptyFilterMessage';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import type { TargetingItemsProps } from '../../types';

type CustomVariablesItemsProps = TargetingItemsProps<CustomVariablesFormState> & {
  /** Show the empty state; defaults to `!readonly`. */
  showEmptyMessage?: boolean;
};

export const CustomVariablesItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  showEmptyMessage = !readonly,
}: CustomVariablesItemsProps) => {
  const { t } = useTranslation();
  const { op, custom: val } = targetingParams;
  const groupValuesByComparisonType = useMemo(() => groupBy(val, key => key.op), [val]);
  const handleChange = useCallback(
    (values: CustomVariablesDataPayload[]) => {
      onChange((prevValues: CustomVariablesFormState) => ({
        ...prevValues,
        custom: values,
      }));
    },
    [onChange]
  );

  const handleRemove = useCallback(
    (comparisonType: string, newState: CustomVariablesDataPayload[]) => {
      const groups = { ...groupValuesByComparisonType };
      groups[comparisonType] = newState;

      handleChange(Object.values(groups).flat());
    },
    [groupValuesByComparisonType, handleChange]
  );

  const emptyComponentRender = useMemo(
    () => showEmptyMessage && <EmptyFilterMessage text={t('No filters applied.')} />,
    [showEmptyMessage, t]
  );

  return val && val.length !== 0 ? (
    <Box data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={t('Custom Variables [ {count} ]', { count: val.length })}
        readonly={readonly}
      />
      <CustomVariablesList
        groupValuesByComparisonType={groupValuesByComparisonType}
        handleRemove={handleRemove}
        readonly={readonly}
        op={op}
      />
    </Box>
  ) : (
    emptyComponentRender
  );
};
